import { Trans, useLingui } from '@lingui/react/macro';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { OnboardingBack, OnboardingExit, PageEyebrow } from '../components/auth-shells.js';
import { Mag } from '../components/mag.js';
import { Select, type SelectOption } from '../components/select.js';
import {
  fetchBrandMarkets,
  fetchMarketDetect,
  saveBrandBrain,
  saveBrandMarkets,
} from '../lib/api.js';
import { useBrandTitle } from '../lib/document-title.js';
import {
  COUNTRY_OPTIONS,
  countryLabel,
  TIMEZONE_OPTIONS,
  tzCityLabel,
} from '../lib/market-options.js';
import { brandBrainQuery, brandsQuery, meQuery } from '../lib/queries.js';
import { refreshQueries } from '../lib/refresh.js';

// Onboarding step 5 — your markets.
//
// New step inserted BEFORE competition (spec multi-market-support-global-
// brands): competition + keyword research are scoped to the markets the
// founder picks here, so they must exist before step 6 runs. The country
// list is the brand's markets (one primary + any additional); the strategist
// researches keywords once per market and competition maps competitors per
// market. The timezone is a SEPARATE, single concern — it's only how mag
// times her check-ins to the founder, not a market (per the s298 reframe).
//
// Save writes brand_markets (POST /api/brands/:id/markets, which syncs the
// brands.target_country primary mirror) + the brain's working_hours_tz, then
// continues to competition.

interface OnboardingMarketsSearch {
  onboarding?: '1';
}

export const Route = createFileRoute('/brand/$slug/onboarding/markets')({
  validateSearch: (search: Record<string, unknown>): OnboardingMarketsSearch => {
    if (search['onboarding'] === '1') return { onboarding: '1' };
    return {};
  },
  beforeLoad: async ({ params, context: { queryClient } }) => {
    const me = await queryClient.ensureQueryData(meQuery());
    if (!me) throw redirect({ to: '/login' });
    const brands = await queryClient.ensureQueryData(brandsQuery());
    const brand = brands.find((b) => b.slug === params.slug);
    if (!brand) throw redirect({ to: '/brands' });
    return { brand };
  },
  component: MarketsStep,
});

function MarketsStep() {
  const { t } = useLingui();

  const { brand } = Route.useRouteContext();
  useBrandTitle(brand.slug, ['onboarding', 'markets']);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const detect = useQuery({ queryKey: ['market-detect'], queryFn: fetchMarketDetect });
  const existing = useQuery({
    queryKey: ['brand-markets', brand.id],
    queryFn: () => fetchBrandMarkets(brand.id),
  });
  const brain = useQuery(brandBrainQuery(brand.id));

  // selected[0] is always the primary market. order = display + primary.
  const [selected, setSelected] = useState<string[]>([]);
  const [tz, setTz] = useState<string>('');
  const [hydrated, setHydrated] = useState(false);

  // Hydrate once all three reads settle: prefer already-saved markets, else
  // seed the primary from detection. tz prefers a real persisted zone over
  // the brain's 'UTC' default, then the detected zone (mirrors MarketAction).
  useEffect(() => {
    if (hydrated) return;
    if (detect.isLoading || existing.isLoading || brain.isLoading) return;
    const saved = existing.data ?? [];
    if (saved.length > 0) {
      setSelected(saved.map((m) => m.country));
    } else if (detect.data?.country) {
      setSelected([detect.data.country]);
    }
    const persistedTz = brain.data?.brandBrain?.preferences?.working_hours_tz ?? null;
    const realPersistedTz = persistedTz && persistedTz !== 'UTC' ? persistedTz : null;
    setTz(realPersistedTz ?? detect.data?.timezone ?? persistedTz ?? '');
    setHydrated(true);
  }, [
    detect.isLoading,
    detect.data,
    existing.isLoading,
    existing.data,
    brain.isLoading,
    brain.data,
    hydrated,
  ]);

  const addable = useMemo<ReadonlyArray<SelectOption<string>>>(
    () => COUNTRY_OPTIONS.filter((o) => !selected.includes(o.value)),
    [selected],
  );

  // Timezone options + the detected/persisted zone prepended if off-list.
  const tzOptions = useMemo<ReadonlyArray<SelectOption<string>>>(() => {
    if (tz && !TIMEZONE_OPTIONS.some((o) => o.value === tz)) {
      return [{ value: tz, label: tzCityLabel(tz), hint: 'detected' }, ...TIMEZONE_OPTIONS];
    }
    return TIMEZONE_OPTIONS;
  }, [tz]);

  const save = useMutation({
    mutationFn: async () => {
      await saveBrandMarkets(
        brand.id,
        selected.map((country, i) => ({ country, isPrimary: i === 0 })),
      );
      // Timezone lives on the brain — spread current preferences so the PUT
      // doesn't wipe sibling fields.
      if (tz && brain.data?.brandBrain) {
        await saveBrandBrain(
          { preferences: { ...brain.data.brandBrain.preferences, working_hours_tz: tz } },
          brand.id,
        );
      }
      await refreshQueries(queryClient, ['brands', 'brand-brain', 'brand-markets']);
    },
    onSuccess: async () => {
      await navigate({
        to: '/brand/$slug/onboarding/competition',
        params: { slug: brand.slug },
        search: { onboarding: '1' as const },
      });
    },
  });

  const detectedLine = (() => {
    const country = detect.data?.country ? countryLabel(detect.data.country) : null;
    const city = detect.data?.timezone ? tzCityLabel(detect.data.timezone) : null;
    return [country, city].filter(Boolean).join(' · ');
  })();

  return (
    <main className="relative min-h-screen bg-[var(--cream)]">
      <OnboardingBack
        to="/brand/$slug/onboarding/assets"
        brandSlug={brand.slug}
        search={{ onboarding: '1' as const }}
      >
        <Trans>back to shelf</Trans>
      </OnboardingBack>
      <OnboardingExit brandSlug={brand.slug} />

      <section className="mx-auto max-w-[680px] px-4 py-12 sm:px-6">
        <header className="flex flex-col items-start gap-4">
          <div
            className="mag-stagger-in flex items-center gap-3"
            style={{ animationDelay: '40ms' }}
          >
            <div className="relative">
              <div
                aria-hidden="true"
                className="mag-aura pointer-events-none absolute inset-[-14px] rounded-full"
              />
              <div className="mag-breathe relative">
                <Mag size={56} mood="casting" trackCursor />
              </div>
            </div>
            <PageEyebrow>
              <Trans>onboarding · step 5 of 6 · your markets</Trans>
            </PageEyebrow>
          </div>
          <h1
            className="mag-stagger-in font-display text-[40px] sm:text-[60px] font-black leading-[0.98] tracking-[-0.045em]"
            style={{ animationDelay: '140ms' }}
          >
            <Trans>
              where do you
              <br />
              <span className="text-[var(--plum)]">sell?</span>
            </Trans>
          </h1>
          <p
            className="mag-stagger-in max-w-[520px] font-serif text-xl leading-tight text-[var(--ink)]/80"
            style={{ animationDelay: '220ms' }}
          >
            <Trans>
              “i research keywords + size up competitors per market. add every country you sell in —
              i'll dig into each one. your first is the primary; i start there.”
            </Trans>
          </p>
        </header>

        <div
          className="mag-stagger-in mt-10 flex flex-col gap-6 rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-5 shadow-[4px_4px_0_var(--spell)] sm:p-6"
          style={{ animationDelay: '320ms' }}
        >
          {detectedLine ? (
            <div className="flex items-center gap-2">
              <span
                aria-hidden="true"
                className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[var(--sage)] font-mono text-[11px] font-bold text-white"
              >
                ✓
              </span>
              <p className="text-[13px] leading-[1.4] text-[var(--ink)]/75">
                <Trans>
                  i detected <span className="font-semibold text-[var(--ink)]">{detectedLine}</span>{' '}
                  — kept as your primary. add more or change below.
                </Trans>
              </p>
            </div>
          ) : null}

          {/* ── markets ── */}
          <div className="flex flex-col gap-2">
            <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--ink)]/55">
              <Trans>markets</Trans>
            </span>
            {selected.length > 0 ? (
              <ul className="flex flex-col gap-2">
                {selected.map((country, i) => (
                  <li
                    key={country}
                    className="flex items-center justify-between gap-3 rounded-xl border border-[var(--ink)]/15 bg-[var(--paper)]/50 px-3.5 py-2.5"
                  >
                    <span className="flex items-center gap-2">
                      <span className="text-[14px] text-[var(--ink)]">{countryLabel(country)}</span>
                      {i === 0 ? (
                        <span className="rounded-md border border-[var(--plum)]/40 bg-[var(--plum)]/8 px-1.5 py-0.5 font-mono text-[9px] font-semibold uppercase tracking-[0.1em] text-[var(--plum)]">
                          <Trans>primary</Trans>
                        </span>
                      ) : null}
                    </span>
                    <span className="flex items-center gap-3">
                      {i !== 0 ? (
                        <button
                          type="button"
                          onClick={() =>
                            setSelected((prev) => [country, ...prev.filter((c) => c !== country)])
                          }
                          className="font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--ink)]/45 transition hover:text-[var(--plum)]"
                        >
                          <Trans>make primary</Trans>
                        </button>
                      ) : null}
                      {selected.length > 1 ? (
                        <button
                          type="button"
                          onClick={() => setSelected((prev) => prev.filter((c) => c !== country))}
                          aria-label={t`remove ${countryLabel(country)}`}
                          className="font-mono text-[11px] uppercase tracking-[0.1em] text-[var(--ink)]/45 transition hover:text-[var(--pulse)]"
                        >
                          <Trans>remove ×</Trans>
                        </button>
                      ) : null}
                    </span>
                  </li>
                ))}
              </ul>
            ) : null}
            {addable.length > 0 ? (
              <Select<string>
                value=""
                onChange={(country) => setSelected((prev) => [...prev, country])}
                options={addable}
                placeholder={selected.length === 0 ? '— pick your market —' : '+ add a market'}
                ariaLabel={t`add a market`}
              />
            ) : null}
          </div>

          {/* ── timezone (separate concern: when mag reaches you) ── */}
          <div className="flex flex-col gap-2 border-t border-[var(--ink)]/10 pt-5">
            <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--ink)]/55">
              <Trans>your timezone</Trans>
            </span>
            <p className="text-[12px] leading-[1.4] text-[var(--ink)]/55">
              <Trans>
                not a market — just so my recaps + emails land during your day, not at 3am.
              </Trans>
            </p>
            <Select<string>
              value={tz}
              onChange={setTz}
              options={tzOptions}
              placeholder={t`— pick —`}
              ariaLabel={t`timezone`}
            />
          </div>

          {save.isError ? (
            <p className="text-[12.5px] text-[var(--pulse)]">
              {save.error instanceof Error ? (
                save.error.message
              ) : (
                <Trans>couldn't save — try again.</Trans>
              )}
            </p>
          ) : null}
        </div>

        <div
          className="mag-stagger-in mt-10 flex items-center justify-end gap-3"
          style={{ animationDelay: '420ms' }}
        >
          <button
            type="button"
            onClick={() => save.mutate()}
            disabled={save.isPending || selected.length === 0}
            className="btn btn-lg disabled:cursor-not-allowed disabled:opacity-50"
          >
            {save.isPending ? <Trans>saving…</Trans> : <Trans>next · competition</Trans>}
            <span className="btn-arrow">→</span>
          </button>
        </div>
      </section>
    </main>
  );
}
